import { useMemo } from 'react';
import { NEW_CANDIDATE_ENDPOINTS, SOURCES } from 'features/new-candidate/constants';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import queryString from 'query-string';
import API, { SECONDARY_API_CLIENT } from 'services/API';
import { ContactIncludes, HiringAuthorityIncludes } from 'services/includes';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { UIStatus } from 'UI/constants/status';

const DEFAULT_PARAMS = {
  apiInstance: null,
  apiVersion: null,
  endpoint: null
};

const PARAMS_MAP = {
  [SOURCES.FROM_TALENT_POOL]: {
    apiInstance: SECONDARY_API_CLIENT,
    apiVersion: 1,
    endpointTemplate: Endpoints.CandidateDataFromApplicant
  },
  [SOURCES.FROM_RESUME]: {
    apiInstance: SECONDARY_API_CLIENT,
    apiVersion: 1,
    endpointTemplate: Endpoints.CandidateDataFromResumeParser
  }
};

const buildParams = ({ canIncludeContactDataSheet, contactId, fromSource, id, isFromDraft }) => {
  if (!id) return DEFAULT_PARAMS;

  if (isFromDraft)
    return {
      apiInstance: SECONDARY_API_CLIENT,
      apiVersion: 1,
      endpoint: NEW_CANDIDATE_ENDPOINTS.draft.replace(':id', id)
    };

  if (!fromSource) return DEFAULT_PARAMS;

  if (fromSource === SOURCES.FROM_DIRECTORY) {
    const isFromContact = Boolean(contactId);

    const baseUrl = isFromContact ? Endpoints.Names : Endpoints.HiringAuthorities;
    const entityIncludes = isFromContact ? ContactIncludes : HiringAuthorityIncludes;
    const additionalIncludes = [
      canIncludeContactDataSheet && isFromContact && 'dataSheetId'
    ].filter(Boolean);

    const includes = [entityIncludes, ...additionalIncludes].filter(Boolean).join(',');

    const endpoint = queryString.stringifyUrl({
      url: `${baseUrl}/${id}`,
      query: { includes }
    });

    return {
      apiInstance: API,
      apiVersion: 2,
      endpoint
    };
  }

  const params = PARAMS_MAP[fromSource];
  if (!params) return DEFAULT_PARAMS;

  const { endpointTemplate, ...rest } = params;

  return {
    ...rest,
    endpoint: String(endpointTemplate).replace(':id', String(id))
  };
};

export const useFetchData = ({ fromSource, loaderParams }) => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const { id, isFromDraft, contactId } = loaderParams;

  const { canIncludeContactDataSheet } = useMemo(
    () => ({
      canIncludeContactDataSheet: checkIfFeatureFlagEnabled(
        FeatureFlags.NewCandidateIncludesContactDataSheet
      )
    }),
    [checkIfFeatureFlagEnabled]
  );

  const { apiInstance, apiVersion, endpoint } = useMemo(
    () =>
      buildParams({
        canIncludeContactDataSheet,
        contactId,
        fromSource,
        id,
        isFromDraft
      }),
    [contactId, canIncludeContactDataSheet, fromSource, id, isFromDraft]
  );

  const { state } = useFetchWithStatusV2({
    endpoint,
    apiInstance,
    apiVersion,
    initialStatus: endpoint ? UIStatus.Loading : UIStatus.Default
  });

  return state;
};
